import React, { useState, useEffect } from 'react';
import { Lead, Tag } from '../types';
import { leadsService, consultoresService } from '../services/api';
import TagManager from './TagManager';

interface LeadModalProps {
  lead: Lead | null;
  onClose: () => void;
}

const LeadModal: React.FC<LeadModalProps> = ({ lead, onClose }) => {
  const [formData, setFormData] = useState({
    numero_processo: '',
    classe: '',
    assunto: '',
    autor_nome: '',
    autor_cpf: '',
    reu_nome: '',
    reu_cpf: '',
    reu_telefone_1: '',
    reu_telefone_2: '',
    reu_telefone_3: '',
    valor_causa: 0,
    localidade: '',
    juiz: '',
    situacao: '',
    status_lead: 'novo',
    consultor: '',
    reu_data_nascimento: '',
    reu_logradouro: '',
    reu_bairro: '',
    reu_cidade: '',
    reu_estado: '',
    reu_cep: '',
    comprou: false,
    r_whats_1: false,
    r_whats_2: false,
    r_whats_3: false,
    observacoes: '',
    tags: [] as Tag[],
    territorio: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [availableTags] = useState<Tag[]>([
    { id: 1, name: 'VIP', color: '#EF4444', created_at: '', updated_at: '' },
    { id: 2, name: 'Urgente', color: '#F59E0B', created_at: '', updated_at: '' },
    { id: 3, name: 'Follow-up', color: '#3B82F6', created_at: '', updated_at: '' },
    { id: 4, name: 'Interessado', color: '#10B981', created_at: '', updated_at: '' },
    { id: 5, name: 'Recontatar', color: '#8B5CF6', created_at: '', updated_at: '' },
  ]);
  const [territories] = useState([
    'São Paulo - Capital',
    'São Paulo - Interior',
    'Rio de Janeiro',
    'Minas Gerais',
    'Paraná',
    'Santa Catarina',
    'Rio Grande do Sul'
  ]);
  const [consultores, setConsultores] = useState<any[]>([]);

  useEffect(() => {
    loadConsultores();
  }, []);

  const loadConsultores = async () => {
    try {
      const response = await consultoresService.getConsultores();
      setConsultores(response.data || response);
    } catch (error) {
      console.error('Erro ao carregar consultores:', error);
    }
  };

  useEffect(() => {
    if (lead) {
      setFormData({
        numero_processo: lead.numero_processo || '',
        classe: lead.classe || '',
        assunto: lead.assunto || '',
        autor_nome: lead.autor_nome || '',
        autor_cpf: lead.autor_cpf || '',
        reu_nome: lead.reu_nome || '',
        reu_cpf: lead.reu_cpf || '',
        reu_telefone_1: lead.reu_telefone_1 || '',
        reu_telefone_2: lead.reu_telefone_2 || '',
        reu_telefone_3: lead.reu_telefone_3 || '',
        valor_causa: lead.valor_causa || 0,
        localidade: lead.localidade || '',
        juiz: lead.juiz || '',
        situacao: lead.situacao || '',
        status_lead: lead.status_lead || 'novo',
        consultor: lead.consultor || '',
        reu_data_nascimento: lead.reu_data_nascimento || '',
        reu_logradouro: lead.reu_logradouro || '',
        reu_bairro: lead.reu_bairro || '',
        reu_cidade: lead.reu_cidade || '',
        reu_estado: lead.reu_estado || '',
        reu_cep: lead.reu_cep || '',
        comprou: lead.comprou || false,
        r_whats_1: lead.r_whats_1 || false,
        r_whats_2: lead.r_whats_2 || false,
        r_whats_3: lead.r_whats_3 || false,
        observacoes: lead.observacoes || '',
        tags: lead.tags || [],
        territorio: lead.territorio || ''
      });
    }
  }, [lead]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target as any;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              name === 'valor_causa' ? parseFloat(value) || 0 : 
              value
    }));
  };

  const handleTagsChange = (tags: Tag[]) => {
    setFormData(prev => ({ ...prev, tags }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (lead) {
        await leadsService.updateLead(lead.id, formData);
      } else {
        await leadsService.createLead(formData);
      }
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">
              {lead ? 'Editar Lead' : 'Nova Lead'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Informações do Processo */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Informações do Processo</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Número do Processo *
                  </label>
                  <input
                    type="text"
                    name="numero_processo"
                    value={formData.numero_processo}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Ex: 0001234-56.2024.8.16.0001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Classe
                  </label>
                  <input
                    type="text"
                    name="classe"
                    value={formData.classe}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Ex: Procedimento do Juizado Especial Cível"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Assunto
                  </label>
                  <input
                    type="text"
                    name="assunto"
                    value={formData.assunto}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Ex: Compra e venda"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Localidade
                  </label>
                  <input
                    type="text"
                    name="localidade"
                    value={formData.localidade}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Ex: São Paulo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Juiz
                  </label>
                  <input
                    type="text"
                    name="juiz"
                    value={formData.juiz}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Nome do juiz responsável"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Situação
                  </label>
                  <input
                    type="text"
                    name="situacao"
                    value={formData.situacao}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Situação atual do processo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Valor da Causa (R$)
                  </label>
                  <input
                    type="number"
                    name="valor_causa"
                    value={formData.valor_causa}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Informações do Réu */}
            <div className="bg-red-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Dados do Réu</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nome do Réu
                  </label>
                  <input
                    type="text"
                    name="reu_nome"
                    value={formData.reu_nome}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Nome completo do réu"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    CPF do Réu
                  </label>
                  <input
                    type="text"
                    name="reu_cpf"
                    value={formData.reu_cpf}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="000.000.000-00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Data de Nascimento
                  </label>
                  <input
                    type="text"
                    name="reu_data_nascimento"
                    value={formData.reu_data_nascimento}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="DD/MM/AAAA"
                  />
                </div>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="comprou"
                      checked={formData.comprou}
                      onChange={handleChange}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Comprado</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Informações do Autor */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Dados do Autor</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Nome do Autor *
                  </label>
                  <input
                    type="text"
                    name="autor_nome"
                    value={formData.autor_nome}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Nome completo do autor"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    CPF do Autor
                  </label>
                  <input
                    type="text"
                    name="autor_cpf"
                    value={formData.autor_cpf}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="000.000.000-00"
                  />
                </div>
              </div>
            </div>

            {/* Contatos */}
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Contatos</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Telefone 1
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      name="reu_telefone_1"
                      value={formData.reu_telefone_1}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="(11) 99999-9999"
                    />
                    <label className="flex items-center mt-1">
                      <input
                        type="checkbox"
                        name="r_whats_1"
                        checked={formData.r_whats_1}
                        onChange={handleChange}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <span className="ml-1 text-xs text-green-600">WhatsApp</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Telefone 2
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      name="reu_telefone_2"
                      value={formData.reu_telefone_2}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="(11) 99999-9999"
                    />
                    <label className="flex items-center mt-1">
                      <input
                        type="checkbox"
                        name="r_whats_2"
                        checked={formData.r_whats_2}
                        onChange={handleChange}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <span className="ml-1 text-xs text-green-600">WhatsApp</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Telefone 3
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      name="reu_telefone_3"
                      value={formData.reu_telefone_3}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder="(11) 99999-9999"
                    />
                    <label className="flex items-center mt-1">
                      <input
                        type="checkbox"
                        name="r_whats_3"
                        checked={formData.r_whats_3}
                        onChange={handleChange}
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <span className="ml-1 text-xs text-green-600">WhatsApp</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {/* Endereço */}
            <div className="bg-yellow-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Endereço do Réu</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Logradouro
                  </label>
                  <input
                    type="text"
                    name="reu_logradouro"
                    value={formData.reu_logradouro}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Rua, número, complemento"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Bairro
                  </label>
                  <input
                    type="text"
                    name="reu_bairro"
                    value={formData.reu_bairro}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    CEP
                  </label>
                  <input
                    type="text"
                    name="reu_cep"
                    value={formData.reu_cep}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="00000-000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Cidade
                  </label>
                  <input
                    type="text"
                    name="reu_cidade"
                    value={formData.reu_cidade}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Estado
                  </label>
                  <input
                    type="text"
                    name="reu_estado"
                    value={formData.reu_estado}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="SP"
                  />
                </div>
              </div>
            </div>

            {/* Status e Gestão */}
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Status e Gestão</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status da Lead
                  </label>
                  <select
                    name="status_lead"
                    value={formData.status_lead}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="novo">Novo</option>
                    <option value="contato_inicial">Contato Inicial</option>
                    <option value="em_negociacao">Em Negociação</option>
                    <option value="proposta_enviada">Proposta Enviada</option>
                    <option value="fechado_ganho">Fechado Ganho</option>
                    <option value="fechado_perdido">Fechado Perdido</option>
                    <option value="arquivado">Arquivado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    👤 Consultor Responsável
                  </label>
                  <select
                    name="consultor"
                    value={formData.consultor}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">Selecionar consultor...</option>
                    {consultores.map(consultor => (
                      <option key={consultor.id} value={consultor.id}>
                        {consultor.name} ({consultor.role})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    🗺️ Território
                  </label>
                  <select
                    name="territorio"
                    value={formData.territorio}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  >
                    <option value="">Selecionar território</option>
                    {territories.map(territory => (
                      <option key={territory} value={territory}>
                        {territory}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="bg-indigo-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4">🏷️ Tags e Categorização</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tags da Lead
                </label>
                <TagManager
                  selectedTags={formData.tags}
                  onTagsChange={handleTagsChange}
                  availableTags={availableTags}
                />
              </div>
            </div>

            {/* Observações */}
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="text-md font-semibold text-gray-900 mb-4">📝 Observações</h4>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Anotações e Observações Específicas
                </label>
                <textarea
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleChange}
                  rows={4}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Digite aqui suas anotações sobre esta lead: contatos feitos, informações importantes, próximos passos, etc."
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeadModal;